import React, { useState } from 'react';
import { Check, Target, Heart, ChevronRight, Loader2, X, Brain, BarChart3, Bot } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface OnboardingModalProps {
  onComplete: () => void;
}

const STEPS = [
  { label: 'Predicciones', icon: Target },
  { label: 'Primer favorito', icon: Heart },
];

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const handleComplete = async () => {
    setSaving(true);
    await supabase.auth.updateUser({ data: { onboarding_complete: true } });
    setSaving(false);
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white">Bienvenido a BetAI</h2>
              <p className="text-sm text-slate-400 mt-0.5">Configura tu experiencia en 2 pasos</p>
            </div>
            <button
              onClick={handleComplete}
              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors"
              title="Omitir"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  i === step
                    ? 'bg-blue-600 text-white'
                    : i < step
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-slate-800 text-slate-500'
                }`}>
                  {i < step
                    ? <Check className="w-3 h-3" />
                    : <s.icon className="w-3 h-3" />
                  }
                  {s.label}
                </div>
                {i < STEPS.length - 1 && (
                  <ChevronRight className="w-3 h-3 text-slate-600 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto">

          {/* Step 0: How predictions work */}
          {step === 0 && (
            <div className="p-6">
              <p className="text-slate-300 text-sm mb-6">
                Cada día generamos predicciones con IA y modelos de Machine Learning para los partidos de tus ligas.
              </p>

              {/* Mock prediction card */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Target className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-xs text-slate-400">Premier League</span>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs font-semibold rounded-full border border-emerald-500/20">
                    Alta confianza
                  </span>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <div className="text-center flex-1">
                    <div className="text-sm font-bold text-white">Arsenal</div>
                    <div className="text-xs text-slate-400">Local</div>
                  </div>
                  <div className="text-center px-3">
                    <div className="text-lg font-bold text-slate-400">vs</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="text-sm font-bold text-white">Chelsea</div>
                    <div className="text-xs text-slate-400">Visitante</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Local', prob: '52%', color: 'blue' },
                    { label: 'Empate', prob: '24%', color: 'slate' },
                    { label: 'Visitante', prob: '24%', color: 'slate' },
                  ].map(item => (
                    <div key={item.label} className={`text-center py-2 rounded-lg ${
                      item.color === 'blue' ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-slate-700/50'
                    }`}>
                      <div className={`text-sm font-bold ${item.color === 'blue' ? 'text-blue-400' : 'text-slate-300'}`}>
                        {item.prob}
                      </div>
                      <div className="text-xs text-slate-500">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { icon: Bot, text: 'Modelo ML entrenado con miles de partidos históricos' },
                  { icon: Brain, text: 'IA analiza lesiones, forma reciente y motivación de equipos' },
                  { icon: BarChart3, text: 'Mercados: 1X2, Over/Under, BTTS, Corners, Tarjetas y más' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                    <item.icon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Save first favorite */}
          {step === 1 && (
            <div className="p-6">
              <p className="text-slate-300 text-sm mb-6">
                Guarda partidos en favoritos para recibir notificaciones y hacer seguimiento de predicciones.
              </p>

              <div className="space-y-3 mb-6">
                {[
                  { icon: '🔔', text: 'Recibe email cuando tu partido favorito esté próximo a comenzar' },
                  { icon: '📈', text: 'Haz seguimiento de si la predicción fue correcta' },
                  { icon: '🏆', text: 'Construye tu historial personal de aciertos' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-slate-800/50 border border-slate-700 rounded-xl">
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-sm text-slate-300">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <Heart className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <p className="text-xs text-blue-300">
                  Puedes guardar partidos desde la página de Partidos o Predicciones tocando el ícono de corazón.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-800">
          {step === 0 && (
            <button
              onClick={() => setStep(1)}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <ChevronRight className="w-4 h-4" />
              Continuar
            </button>
          )}
          {step === 1 && (
            <button
              onClick={handleComplete}
              disabled={saving}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? 'Guardando...' : '¡Listo! Empezar a explorar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
